import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { SwalComponent } from "@sweetalert2/ngx-sweetalert2";
import { AuthService } from "app/core/services/admin/auth/auth.service";
import { DataService } from "app/core/services/global/data/data.service";
import { LayoutService } from "app/core/services/global/layout/layout.service";
import { SwalModalService } from "app/core/services/global/modal/modal.service";
import { ToastService } from "app/core/services/global/toast/toast.service";
import { ListService } from "app/core/services/pim/list.service";
import { ActionButtonsDef, ActionEvent, ActionType } from "app/shared/components/action-buttons";
import { TableView } from "app/shared/components/table";
import { MaestroLists } from "app/shared/models";
import { ACL } from "app/shared/models/acl";
import { TableDataPipe } from "app/shared/pipes/table-data.pipe";
import { Subscription } from "rxjs";

@Component({
    selector: "app-list-list",
    templateUrl: "./list-list.component.html",
})
export class ListListComponent implements TableView, OnInit, OnDestroy {
    table: [string, string][];
    columns: string[];

    private actionButtonSource: Subscription;

    private _searchSubscription: Subscription;

    private _lists: MaestroLists;

    history: any;
    showDiff: boolean;
    startHistory: number;
    elementStory: number;
    objectKeys = Object.keys;

    @ViewChild("historyModal", { static: true }) private swal: SwalComponent;

    readonly actions: ActionButtonsDef = [];

    acl: ACL;

    private importButtonSource: Subscription;

    constructor(
        private _listService: ListService,
        private _elementTableDataPipe: TableDataPipe,
        private _modalService: SwalModalService,
        private _translateService: TranslateService,
        private _router: Router,
        private _route: ActivatedRoute,
        private _layout: LayoutService,
        private _authService: AuthService,
        private _dataService: DataService,
        private _toastService: ToastService
    ) {}

    ngOnInit(): void {
        this.acl = this._authService.getUserAclFromToken();

        if (this.acl.SYSPAD_PIM_LIST_CREATE) {
            this._layout.actionButton.enable = true;
            this._layout.actionButton.title = "pim.lists.create";
            this.actionButtonSource = this._layout.actionButton.click$.subscribe((_) => this.onCreate());

            this._layout.importButton.enable = true;
            this._layout.importButton.title = "general.import";
            this.importButtonSource = this._layout.importButton.click$.subscribe((_) => this.onImport());
        }
        if (this.acl.SYSPAD_PIM_LIST_UPDATE) {
            this.actions.push({ type: ActionType.Update, right: "SYSPAD_PIM_LIST_UPDATE" });
        }
        /*if (this.acl.SYSPAD_PIM_ELEMENT_READ) {
            this.actions.push({ type: ActionType.Story, right: "SYSPAD_PIM_ELEMENT_READ"});
        }*/
        if (this.acl.SYSPAD_PIM_LIST_DELETE) {
            this.actions.push({ type: ActionType.Delete, right: "SYSPAD_PIM_LIST_DELETE" });
        }
        this._lists = this._route.snapshot.data.lists.data;
        this.onFilter(this._listService.filterSearch.value);
        this._layout.breadcrumb.setPath({ routerLink: null, name: "breadcrumb.pim.lists.title" }, 1);
        this._layout.searchInput.enable = true;
        this._layout.searchInput.currentValue = this._listService.filterSearch.value;

        this._searchSubscription = this._layout.searchInput.filter$.subscribe((value) => {
            this.onFilter(value);
            this._layout.searchInput.currentValue = this._listService.filterSearch.value;
        });
    }

    /**
     * Filter by list name's
     * @param value
     * @returns
     */
    onFilter(value: string): void {
        if (value.length < 3) {
            this._initList(this._lists);
            return;
        }
        this._listService.filterSearch.next(value);
        this._initList(
            this._lists.filter((list) => {
                return list.name.toLowerCase().includes(value.toLowerCase());
            })
        );
    }

    ngOnDestroy(): void {
        this._layout.breadcrumb.setPath(null, 1);
        this._layout.actionButton.enable = false;
        this._layout.actionButton.title = null;
        if (this.actionButtonSource) {
            this.actionButtonSource.unsubscribe();
        }
        this._searchSubscription.unsubscribe();
        this._layout.searchInput.enable = false;

        if (this.importButtonSource) {
            this.importButtonSource.unsubscribe();
        }

        this._layout.importButton.enable = false;
        this._layout.importButton.title = null;
    }

    onCreate() {
        this._openCreateModal();
    }

    onActionClick(event: ActionEvent) {
        const id = event.rowId;
        switch (event.action) {
            case ActionType.Delete:
                this._delete(id);
                break;
            case ActionType.Update:
                this._router.navigate(["update", id], { relativeTo: this._route });
                break;
            case ActionType.View:
                this._router.navigate(["view", id], { relativeTo: this._route });
                break;
            case ActionType.Story:
                this._listService.getStory(id, 0).subscribe((data) => {
                    this.getStory(data);
                });
                break;
        }
    }

    /**
     * Init the list
     *
     * @param elementTypes
     */
    private _initList(elementTypes: MaestroLists) {
        const formatedData = this._elementTableDataPipe.transform(elementTypes);
        this.columns = formatedData[0];
        this.table = formatedData[1];
    }

    /**
     * Delete a list
     * @param id
     */
    private _delete(id: number) {
        this._listService.deleteList(id).subscribe((_) => {
            this._modalService.success(this._translateService.instant("general.deleted"));
            this._listService.getLists().subscribe((lists: any) => this._initList(lists.data));
        });
    }

    /**
     * Create a list
     *
     * @param name
     * @param open
     */
    private _create(name, open = false) {
        if (name) {
            this._listService.createList(name).subscribe(
                (data: any) => {
                    this._modalService.success(this._translateService.instant("general.created"));
                    if (open) {
                        this._router.navigate(["update", data.data.id], { relativeTo: this._route });
                    } else {
                        this._listService.getLists().subscribe((elements: any) => this._initList(elements.data));
                    }
                },
                (response) => {
                    response = response.error;
                    this._modalService.error(this._translateService.instant("response.error." + response.key + "." + response.method, { data: response.data }));
                }
            );
        }
    }

    private _openCreateModal() {
        return this._modalService
            .open({
                title: this._translateService.instant("lists.create"),
                showCloseButton: true,
                confirmButtonText: this.acl.SYSPAD_PIM_LIST_UPDATE ? this._translateService.instant("general.create_open") : this._translateService.instant("general.create"),
                confirmButtonColor: "$maestro",
                showCancelButton: true,
                cancelButtonText: this._translateService.instant("general.cancel"),
                cancelButtonColor: "#FF0000",
                html: '<div class="form-group">' + '<input class="form-control" type="text" placeholder="" id="ModalFieldsetTitle">' + "</div>",
            })
            .then((result) => {
                if (result.value) {
                    this._create($("#ModalFieldsetTitle").val(), result.dismiss || !this.acl.SYSPAD_PIM_LIST_UPDATE ? false : true);
                }
            });
    }

    /**
     * Get All Element's Story
     * @param id
     */
    getStory(data: any) {
        this.history = [];
        if (data.type === "success") {
            this.history = data.data.story;
            this.elementStory = data.data.id;
            this.startHistory = 10;
        }
        this.swal.fire();
    }

    onImport() {
        const infos = [this._translateService.instant("import.columnInfos.list"), this._translateService.instant("import.columnInfos.value")];

        this._modalService.import("import-list-base.xlsx", infos, "list").then((fileData) => {
            if (fileData) {
                this._dataService.convertFile(fileData).subscribe((res: any) => {
                    const file = { filename: fileData.name, mimeType: fileData.type, size: fileData.size, base64: res };

                    this._listService.importFromFile(file).subscribe((res: any) => {
                        if (res.type === "success") {
                            this._toastService.show({ message: this._translateService.instant("import.succeeds"), type: "success" });
                            this._listService.getLists().subscribe((elements: any) => this._initList(elements.data));
                        }
                    });
                });
            }
        });
    }
}
